/* tslint:disable */
/* eslint-disable */
/**
 * REAP API
 * Public REST API for REAP (multi-tenant, agentic AI for network operations).  ### Identifier conventions - **id**: UUID — canonical and used for foreign keys and writes. - **slug**: immutable, human-readable handle (kebab-case) used in configs/URLs. Unique per scope (org) or globally (orgs). - **name**: human-friendly display label (mutable). Do **not** use as a reference key.  ## Authentication Overview  This API supports local credentials (password, passkeys), SSO (OIDC/SAML), and email-verified bootstraps for registration/reset/invitations. The design is enumeration-resistant and minimizes clicks for the common case (daily sign-in).  ### Daily sign-ins (no emailTx required) - **Password**: `POST /auth/password/verify` with `{ email, password }` → returns **loginTx**. - **Passkey (email-first)**: `POST /auth/passkeys/assert` with `{ email }` → WebAuthn options + `challengeToken`   → `POST /auth/passkeys/assert/verify` → **loginTx**. - **Usernameless passkey** (optional): `POST /auth/passkeys/assert` with `{ usernameless: true }`.  Exchange **loginTx** at `POST /auth/token`: - If already org-bound (SSO, single org, or server inference) → returns **TokenResponse**. - If not org-bound and multiple orgs exist → returns **200** with `{ nextStep: \"pickOrg\", memberships:[...] }`.   The server **SHOULD filter out ineligible orgs** so the client can re-call `/auth/token` with `orgId`   without additional policy checks. - To **switch orgs** later, call `POST /auth/token` with `grantType:\"refresh\"` and `orgId`.  ### SSO (OIDC/SAML) - IdP flows produce **`oidcTx`** or **`samlTx`**, which you exchange at `POST /auth/token`. - HRD (home realm discovery): `POST /auth/discover/start` always replies **200** with a discriminated   `nextStep`:     - `sso_redirect` (single, safe target) — redirect using `connection.startUrl` (optionally include `login_hint`).     - `picker` (multiple targets) — render an org/connection picker.     - `verify_email` — prove mailbox control before revealing org/connection detail.   *No membership/policy status leaks at Start; the rich, email-proven view is returned by `/auth/discover/verify`.*  Email verification is a common bootstrap across Discovery and Invitations. Invitation Accept returns emailTx plus invited-org context (policy and SSO connections) without enumerating other orgs. Clients should drive UX from these fields and exchange loginTx/oidcTx/samlTx at `/auth/token`.  ### Email-verified bootstraps (keep `emailTx`) - **Register password/passkey**, **reset password**, and **invitation acceptance** require proving email ownership.   After `/auth/discover/verify` (or `/auth/invitations/accept`) you get **`emailTx`** for these operations or to   obtain a limited pre-org session at `/auth/token` (for example, to create the first org).  ### Enumeration resistance & security - Return generic errors for failed sign-in; normalize response timing and apply rate-limits with small jitter. - Avoid revealing user/org status or policy in pre-verification responses. - Use HTTPS; set appropriate cache controls; consider device-bound refresh tokens and short-lived access tokens.  ### Client authentication REAP supports two client auth mechanisms:  - **Bearer JWT** (`Authorization: Bearer <jwt>`): short-lived, user/session tokens returned by `/auth/token`.   Backends validate these locally via signature/claims.  - **API Key** (`X-Api-Key: <secret>`): long-lived, org-scoped secrets intended for automation (CI/CD, headless jobs).   Keys are validated by REAP’s auth layer; they are not JWTs and should not be sent as Bearer tokens.  **Guidance** - Prefer **Bearer JWTs** for interactive UIs and short-lived clients. - Use **API keys** for machine-to-machine integrations; rotate or **PATCH** `expiresAt` to extend, and **DELETE** (revoke) when done. - Assign permissions via **roles**: provide `roleIds` on create/patch; responses include `rolesResolved` for admin UX. - Do not place API keys in URLs or logs. Keys are shown **once** on creation.  ### API key return semantics - The full API key **secret is returned only in the 201 Create response** of `/orgs/{orgId}/api-keys`. - It is **never** returned by GET/PATCH/DELETE. - Clients should display it once and prompt the operator to store it securely (password manager, vault). - For subsequent requests, present the key as `X-Api-Key: <secret>`; do **not** use the Bearer scheme for API keys.  ## Auth policies (overview) - **SSO JIT (just-in-time) provisioning** can be enabled per org: SSO users can sign in without invitations. - **Invite-only SSO** (optional): require an invitation before SSO users can join the org. - **Local accounts** (optional): allow bootstrap/break-glass users without SSO. - **Discover** avoids user enumeration; org memberships and sign-in options are revealed only after email verification. - **Invitations** never expose tokens/links via API; the backend emails the invite link to the recipient. - **Org auth policy** is a **singleton** at `/orgs/{orgId}/auth/policy` that controls JIT, invite-only SSO, and local-account allowances.   The policy is retrieved/updated via **GET/PUT** and versioned with **ETag**.  ## Discovery result (structure) - **Local methods are global**: `DiscoverVerifyResponse.localConnections` lists user-level methods (`passkey`, `password`) available for the verified email — shown **once** in the UI. - **SSO is per-org**: `memberships[].connections` lists **org-scoped** SSO options (`oidc`, `saml`). This array may be **empty** if the org has no SSO. - **Policy hint**: `memberships[].policy.requiresSso` indicates that the org **requires** SSO (local login should not grant access there), even if local methods exist globally.  ## Passwordless authentication (overview) - New-user and invitation flows converge on **email verification** → short-lived **`emailTx`** (no user enumeration). - Clients then use **Passkeys** (WebAuthn/FIDO2) to enroll a credential (attestation) or sign in (assertion). - Successful passkey assertion yields **`loginTx`**, which is exchanged at **`POST /auth/token`** with `grantType=login_tx`. - **Token grants** supported at `/auth/token`:     - **authorization_code** (OIDC) via **`oidcTx`**     - **saml2_bearer** (SAML) via **`ssoTx`**     - **login_tx** (Passkey) via **`loginTx`**     - **refresh_token** (rotation; optional `orgId` switch)     - **email_tx** (bootstrap pre-org; limited to safe endpoints like creating the first org) - **Stateless challenge** option for passkeys: options responses include a signed **`challengeToken`** that the client must echo on verify,   allowing the server to validate the challenge/guardrails **without storing per-challenge state** (short TTL recommended). - **Optional fast-path**: `POST /auth/passkeys/attest/verify?issueLoginTx=true` may return `loginTx` immediately after registration   (policy permitting), otherwise the default flow is attest → assert → token. - **Usernameless (passkey-first) sign-in**: the portal may skip email/Discover and call   `POST /auth/passkeys/assert` with `{ \"usernameless\": true }`. The server returns   `PublicKeyCredentialRequestOptions` with an empty/omitted `allowCredentials`. The browser’s   Conditional UI prompts if a matching passkey exists; on success, the client verifies at   `/auth/passkeys/assert/verify` to obtain `loginTx`, then exchanges at `/auth/token`.  ## API response conventions - **No envelopes**: endpoints return typed bodies directly. - **Resources**: single GET/POST returns the resource object (composed from `ResourceBase` and `OrgScoped` when applicable). - **Operational endpoints** (e.g., auth/token, passkeys) return typed operation results (no `ResourceBase`). - **Lists**: return `{ items: [...], meta: { ...} }`, where each item is a full resource object and `meta` follows **PageMeta**. Responses MAY include `Link` headers (`rel=\"next\"`, `rel=\"prev\"`). When `meta.exactTotal` is omitted clients SHOULD treat totals as unknown and render “more available” messaging rather than fabricating counts.  ### Cache-Control conventions - **Mutable resource detail** (single GET, POST, PATCH) and every response that carries credentials, tokens, or bootstrap data sends `Cache-Control: no-store`. Browsers MUST revalidate before rendering drawers/forms so operators see their latest edits. - **Collection/list endpoints** that back dashboards or tables may use `Cache-Control: private, max-age=15` together with `ETag` and `Link` headers. Clients SHOULD revalidate on manual refresh (If-None-Match) and treat the TTL as an upper bound. - **Streaming/event feeds** and download endpoints for bootstrap bundles always respond with `Cache-Control: no-store`. - If a response omits `Cache-Control`, treat it as non-cacheable.  ### Identity & scoping - Every object is keyed by `id` (UUID). - Common audit fields come from `ResourceBase` (`createdAt`, `updatedAt`, `createdBy`, `updatedBy`). - Multi-tenant scoping builds on `OrgScoped` (adds `orgId`); request bodies never include `orgId`—it comes from the path.  ### Pagination & sorting - Pagination via `limit` (1–200, default 50), `after`, `before`. Servers SHOULD return `Link` headers that mirror `meta.next`/`meta.prev` and include `meta.exactTotal` when a precise count is available. - `meta.exactTotal` is OPTIONAL. When omitted it signals “total unknown”; when the repository already computes a count (e.g., `COUNT(*)`) servers SHOULD populate it. - Sort (when supported) is a comma-separated list; prefix with `-` for descending (for example `-lastSeenAt,mgmtAddress`).  ### Concurrency & caching - Mutations return **ETag**; `PATCH`/`DELETE` require **If-Match** (mismatch → **412**). - `GET` supports **If-None-Match** and may return **304 Not Modified**. - Each response includes **`X-Request-Id`** for troubleshooting.  ### Conditional GETs (ETag / If-None-Match) - Servers return **ETag** headers on GETs for cache-friendly revalidation. - Clients can send **If-None-Match** with the last seen ETag:   - If representation is unchanged → **304 Not Modified** (no body).   - If changed → **200 OK** with a new ETag and the latest representation. - Recommended for frequently-polled resources (e.g., devices list, discovery runs, topology graphs).  ### Error model - Structured errors use the `Problem` schema. - Common statuses: **400** (validation), **401/403** (auth/authz), **404** (not found), **409** (conflict), **412** (ETag mismatch), **429** (rate limit).  ### Credentials model (overview) - **Secret vs Credential vs Assignment**   - **Secret**: write-only value + metadata (`type`, `fingerprint`, `last4`). Value is never returned.   - **Credential**: one access **kind** (`cli_ssh`, `snmp_v2c`, `snmp_v3`, `gnmi`, `api_rest`) with kind-specific fields that **reference Secret IDs** (e.g., SSH key, SNMP passwords, API key, mTLS certs).   - **Credential Assignment**: routing rule that applies a **single credential** by scope (org / sites / specific devices) with optional match filters (hostname regex/prefix/suffix, labels, vendor/model).   - Rotate a secret’s value via `POST /value` (use **If-Match** to guard against concurrent rotation). - **Validation**   - On create/update, the server validates that each referenced Secret ID is of the **expected SecretType** for the credential kind (mismatch → 400). - **Resolution order (when multiple assignments match a device)**   1. Lower `priority` first (e.g., 10 wins over 100)   2. Narrower `scope` (device > sites > org)   3. Most recent `updatedAt` - **Consistency**   - Single-resource endpoints return the resource (no envelopes).   - List endpoints return `{ items, meta }` where `meta` follows `PageMeta` and cursors are passed via `limit/after/before`. - **Concurrency & cache**   - Mutations return **ETag**; `PATCH`/`DELETE` require **If-Match**.   - `GET` supports **If-None-Match** to return **304 Not Modified** when ETag matches.  ## Devices (key behaviors)  - **States** — `discovered`, `validated`, `unreachable`, `retired`, `merged` (see `DeviceState`). - **Discovery-managed fields** (for example `vendor`, `model`, `osVersion`, `serial`, `hostname`, `firstSeenAt`, `lastSeenAt`) are **read-only** via API. - **User-editable** fields: `displayName`, `labels` (case-insensitive keys; PATCH overwrites the map), `role` (free text, e.g. `access`, `core`, `firewall`, `wireless-ap`), `notes`. - **Create** is allowed (beta/testing). Default list sort: `-lastSeenAt`. - **Retire** via `PATCH` (`state=retired`). **DELETE** is hard-delete (beta convenience). - **Merge** consolidates duplicates; merged-away device IDs subsequently return **404**.  ## Edge connectivity (Connectors & Connector Clusters)  **Connectors** are lightweight agents (AMI/OVA/etc.) that establish outbound, persistent control channels from customer sites to REAP. Connectors register to an **Organization**, can optionally belong to a **Connector Cluster**, and periodically report health, version, and capabilities.  **Connector Clusters** group one or more connectors for redundancy or capacity. Clusters can be attached to **Sites** (N↔M). The API allows make-before-break migrations: a Site can temporarily be served by multiple clusters. Backend scheduling ensures only one active worker set manages a Site at a time. - The attach result includes **multiAttached** to surface Sites now attached to this cluster and at least one other cluster.  **User-data (first-boot)**: each Connector exposes a one-time, short-lived bootstrap bundle for cloud-init. Content negotiation: - `Accept: text/cloud-config` → cloud-init YAML (default) - `Accept: application/json` → JSON wrapper (opaque to clients) - `Accept: application/base64+json` → base64 of the opaque JSON  One-time semantics: redeemed → **409 Conflict**, expired → **410 Gone**. Responses SHOULD include `Cache-Control: no-store`.  **Concurrency & caching**: mutable single-resource endpoints return **ETag** and require **If-Match** on updates/deletes. Reads MAY accept **If-None-Match** and return **304**. Lists use pagination via `{ items, meta }` (see `PageMeta`).  # SSO / IdP Connections REAP supports per-organization **IdP connections** for **OIDC** and **SAML**. - Connections are modeled as a discriminated union (`type: \"oidc\" | \"saml\"`). - Connection **config** holds IdP metadata. Some fields are **readOnly** and computed by REAP (e.g., `redirectUris` for OIDC; `acsUrl`, `spEntityId` for SAML). - **Concurrency:** single-resource GET returns **ETag**; **PATCH**_/_**DELETE** require **If-Match**. - **Deletes** return **204 No Content**. - **SAML IdP-initiated** sign-in can be allowed/blocked by `IdpSamlConfig.allowIdpInitiated` (default **true**). # OIDC client authentication - Confidential clients only: REAP supports client_secret_basic (default) and client_secret_post. - private_key_jwt and public clients are not supported in this version. - PKCE is supported but off by default; enable per connection via `config.enforcePkce`.  ## Local password authentication (optional) - Fits the same **email-first, enumeration-resistant** pattern: verify email → short-lived **`emailTx`**. - Supports:   - **Register** a password after email verification   - **Verify** password to obtain **`loginTx`** (exchange at `/auth/token` with `grantType=loginTx`)   - **Reset** password (email-verified), **Change** password (bearer session) - Client sends the **clear-text password over HTTPS**; the server performs KDF hashing (e.g., **Argon2id** with per-user salt; optional application-level pepper in KMS). - Store the **password hash (and parameters)** in the **regular user table** (not a secrets manager). If you use a **pepper**, store that pepper as a secret. - Error hardening:   - Same **lockout** semantics as passkeys (use **423** with a neutral error body)   - Consistent, non-enumerating responses for all email-bound flows
 *
 * The version of the OpenAPI document: 1.3.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as runtime from '../runtime';
import { CMDBAttributeMappingProfileFromJSON, CMDBAttributeMappingRulesRequestToJSON, CMDBAuthorityPolicyFromJSON, CMDBAuthorityPolicyPreviewFromJSON, CMDBAuthorityRulesRequestToJSON, CMDBBlastRadiusResponseFromJSON, CMDBDependencyGroupFromJSON, CMDBDependencyGroupsRequestToJSON, CMDBDeviceCrosswalkResponseFromJSON, CMDBDiscrepancyReportFromJSON, CMDBEffectiveAssertionFromJSON, CMDBFindingHistoryFromJSON, CMDBFindingTransitionRequestToJSON, CMDBMaturityDeclarationFromJSON, CMDBMaturityDeclarationRequestToJSON, CMDBMaturityScoreFromJSON, CMDBMeasuredMaturityFromJSON, CMDBPublicationPolicyFromJSON, CMDBPublicationPolicyUpdateRequestToJSON, CMDBPublicationProposalFromJSON, CMDBPublicationProposalCreateRequestToJSON, CMDBPublicationProposalDecisionRequestToJSON, CMDBReconciliationFindingFromJSON, CMDBReconciliationObservationFromJSON, CMDBReconciliationPolicyFromJSON, CMDBReconciliationPolicyUpdateRequestToJSON, CMDBReconciliationRequestFromJSON, CMDBReconciliationRunFromJSON, IntegrationConnectionCMDBUnboundDevicesResponseFromJSON, InventoryCMDBDiscrepancyReportFromJSON, } from '../models/index';
/**
 *
 */
export class CMDBReconciliationApi extends runtime.BaseAPI {
    /**
     * Atomically activate a reviewed attribute mapping profile
     */
    activateCMDBAttributeMappingProfileRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling activateCMDBAttributeMappingProfile().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling activateCMDBAttributeMappingProfile().');
            }
            if (requestParameters['profileId'] == null) {
                throw new runtime.RequiredError('profileId', 'Required parameter "profileId" was null or undefined when calling activateCMDBAttributeMappingProfile().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/attribute-mapping/{profileId}/activate`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"profileId"}}`, encodeURIComponent(String(requestParameters['profileId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBAttributeMappingProfileFromJSON(jsonValue));
        });
    }
    /**
     * Atomically activate a reviewed attribute mapping profile
     */
    activateCMDBAttributeMappingProfile(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.activateCMDBAttributeMappingProfileRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Clone the active attribute mapping into a draft
     */
    createCMDBAttributeMappingDraftRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling createCMDBAttributeMappingDraft().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling createCMDBAttributeMappingDraft().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/attribute-mapping`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBAttributeMappingProfileFromJSON(jsonValue));
        });
    }
    /**
     * Clone the active attribute mapping into a draft
     */
    createCMDBAttributeMappingDraft(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.createCMDBAttributeMappingDraftRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Create a draft authority policy for a scope
     */
    createCMDBAuthorityPolicyDraftRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling createCMDBAuthorityPolicyDraft().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling createCMDBAuthorityPolicyDraft().');
            }
            if (requestParameters['scopeKey'] == null) {
                throw new runtime.RequiredError('scopeKey', 'Required parameter "scopeKey" was null or undefined when calling createCMDBAuthorityPolicyDraft().');
            }
            const queryParameters = {};
            if (requestParameters['scopeKey'] != null) {
                queryParameters['scopeKey'] = requestParameters['scopeKey'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/authority-policies`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBAuthorityPolicyFromJSON(jsonValue));
        });
    }
    /**
     * Create a draft authority policy for a scope
     */
    createCMDBAuthorityPolicyDraft(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.createCMDBAuthorityPolicyDraftRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Create governed publication proposals from REAP evidence
     */
    createCMDBPublicationProposalsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling createCMDBPublicationProposals().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling createCMDBPublicationProposals().');
            }
            if (requestParameters['cMDBPublicationProposalCreateRequest'] == null) {
                throw new runtime.RequiredError('cMDBPublicationProposalCreateRequest', 'Required parameter "cMDBPublicationProposalCreateRequest" was null or undefined when calling createCMDBPublicationProposals().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/publication/proposals`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBPublicationProposalCreateRequestToJSON(requestParameters['cMDBPublicationProposalCreateRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CMDBPublicationProposalFromJSON));
        });
    }
    /**
     * Create governed publication proposals from REAP evidence
     */
    createCMDBPublicationProposals(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.createCMDBPublicationProposalsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Approve, reject, or cancel a governed publication proposal
     */
    decideCMDBPublicationProposalRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling decideCMDBPublicationProposal().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling decideCMDBPublicationProposal().');
            }
            if (requestParameters['proposalId'] == null) {
                throw new runtime.RequiredError('proposalId', 'Required parameter "proposalId" was null or undefined when calling decideCMDBPublicationProposal().');
            }
            if (requestParameters['cMDBPublicationProposalDecisionRequest'] == null) {
                throw new runtime.RequiredError('cMDBPublicationProposalDecisionRequest', 'Required parameter "cMDBPublicationProposalDecisionRequest" was null or undefined when calling decideCMDBPublicationProposal().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/publication/proposals/{proposalId}/decision`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"proposalId"}}`, encodeURIComponent(String(requestParameters['proposalId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBPublicationProposalDecisionRequestToJSON(requestParameters['cMDBPublicationProposalDecisionRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBPublicationProposalFromJSON(jsonValue));
        });
    }
    /**
     * Approve, reject, or cancel a governed publication proposal
     */
    decideCMDBPublicationProposal(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.decideCMDBPublicationProposalRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Record a customer maturity declaration
     */
    declareCMDBMaturityRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling declareCMDBMaturity().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling declareCMDBMaturity().');
            }
            if (requestParameters['cMDBMaturityDeclarationRequest'] == null) {
                throw new runtime.RequiredError('cMDBMaturityDeclarationRequest', 'Required parameter "cMDBMaturityDeclarationRequest" was null or undefined when calling declareCMDBMaturity().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/maturity-declarations`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBMaturityDeclarationRequestToJSON(requestParameters['cMDBMaturityDeclarationRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBMaturityDeclarationFromJSON(jsonValue));
        });
    }
    /**
     * Record a customer maturity declaration
     */
    declareCMDBMaturity(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.declareCMDBMaturityRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get the current attribute mapping profile
     */
    getCMDBAttributeMappingProfileRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getCMDBAttributeMappingProfile().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getCMDBAttributeMappingProfile().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/attribute-mapping`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBAttributeMappingProfileFromJSON(jsonValue));
        });
    }
    /**
     * Get the current attribute mapping profile
     */
    getCMDBAttributeMappingProfile(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getCMDBAttributeMappingProfileRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Compute a read-only blast radius from a REAP device through impact-enabled CMDB relationships
     */
    getCMDBBlastRadiusRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getCMDBBlastRadius().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getCMDBBlastRadius().');
            }
            if (requestParameters['deviceId'] == null) {
                throw new runtime.RequiredError('deviceId', 'Required parameter "deviceId" was null or undefined when calling getCMDBBlastRadius().');
            }
            const queryParameters = {};
            if (requestParameters['deviceId'] != null) {
                queryParameters['deviceId'] = requestParameters['deviceId'];
            }
            if (requestParameters['depth'] != null) {
                queryParameters['depth'] = requestParameters['depth'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/blast-radius`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBBlastRadiusResponseFromJSON(jsonValue));
        });
    }
    /**
     * Compute a read-only blast radius from a REAP device through impact-enabled CMDB relationships
     */
    getCMDBBlastRadius(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getCMDBBlastRadiusRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Compare REAP devices with active network CI evidence for one CMDB connection
     */
    getCMDBDeviceCrosswalkRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getCMDBDeviceCrosswalk().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getCMDBDeviceCrosswalk().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/crosswalk`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBDeviceCrosswalkResponseFromJSON(jsonValue));
        });
    }
    /**
     * Compare REAP devices with active network CI evidence for one CMDB connection
     */
    getCMDBDeviceCrosswalk(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getCMDBDeviceCrosswalkRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns the latest CMDB maturity snapshot for the organization. When `connectionId` is supplied, the response is scoped to that CMDB connection. If no persisted snapshot exists yet, the backend may derive one from the latest discrepancy analysis.
     * Get CMDB maturity snapshot
     */
    getCMDBMaturityRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getCMDBMaturity().');
            }
            const queryParameters = {};
            if (requestParameters['connectionId'] != null) {
                queryParameters['connectionId'] = requestParameters['connectionId'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/cmdb/maturity`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBMaturityScoreFromJSON(jsonValue));
        });
    }
    /**
     * Returns the latest CMDB maturity snapshot for the organization. When `connectionId` is supplied, the response is scoped to that CMDB connection. If no persisted snapshot exists yet, the backend may derive one from the latest discrepancy analysis.
     * Get CMDB maturity snapshot
     */
    getCMDBMaturity(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getCMDBMaturityRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get measured CMDB maturity for the connection
     */
    getCMDBMeasuredMaturityRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getCMDBMeasuredMaturity().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getCMDBMeasuredMaturity().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/maturity`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBMeasuredMaturityFromJSON(jsonValue));
        });
    }
    /**
     * Get measured CMDB maturity for the connection
     */
    getCMDBMeasuredMaturity(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getCMDBMeasuredMaturityRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get governed publication policy for a CMDB connection
     */
    getCMDBPublicationPolicyRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getCMDBPublicationPolicy().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getCMDBPublicationPolicy().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/publication/policy`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBPublicationPolicyFromJSON(jsonValue));
        });
    }
    /**
     * Get governed publication policy for a CMDB connection
     */
    getCMDBPublicationPolicy(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getCMDBPublicationPolicyRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Get one governed publication proposal
     */
    getCMDBPublicationProposalRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getCMDBPublicationProposal().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getCMDBPublicationProposal().');
            }
            if (requestParameters['proposalId'] == null) {
                throw new runtime.RequiredError('proposalId', 'Required parameter "proposalId" was null or undefined when calling getCMDBPublicationProposal().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/publication/proposals/{proposalId}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"proposalId"}}`, encodeURIComponent(String(requestParameters['proposalId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBPublicationProposalFromJSON(jsonValue));
        });
    }
    /**
     * Get one governed publication proposal
     */
    getCMDBPublicationProposal(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getCMDBPublicationProposalRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns the org-level CMDB reconciliation policy, including explicit relation mapping rules that control how raw CMDB relations are interpreted for comparison with REAP.
     * Get org CMDB reconciliation policy
     */
    getCMDBReconciliationPolicyRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getCMDBReconciliationPolicy().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/cmdb/reconciliation-policy`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBReconciliationPolicyFromJSON(jsonValue));
        });
    }
    /**
     * Returns the org-level CMDB reconciliation policy, including explicit relation mapping rules that control how raw CMDB relations are interpreted for comparison with REAP.
     * Get org CMDB reconciliation policy
     */
    getCMDBReconciliationPolicy(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getCMDBReconciliationPolicyRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns a read-only discrepancy report for the selected CMDB connection. The report combines current bindings, pending action items, and synced CMDB projection coverage to estimate CMDB maturity and highlight gaps between REAP network reality and CMDB state.
     * Summarize CMDB discrepancies for inventory workflows
     */
    getInventoryCMDBDiscrepancyReportRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getInventoryCMDBDiscrepancyReport().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getInventoryCMDBDiscrepancyReport().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/discrepancies`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => InventoryCMDBDiscrepancyReportFromJSON(jsonValue));
        });
    }
    /**
     * Returns a read-only discrepancy report for the selected CMDB connection. The report combines current bindings, pending action items, and synced CMDB projection coverage to estimate CMDB maturity and highlight gaps between REAP network reality and CMDB state.
     * Summarize CMDB discrepancies for inventory workflows
     */
    getInventoryCMDBDiscrepancyReport(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getInventoryCMDBDiscrepancyReportRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns the latest persisted discrepancy report for the selected CMDB connection. If no report has been generated yet, the backend may derive one on demand and persist it.
     * Get the latest persisted CMDB discrepancy report for an inventory CMDB connection
     */
    getInventoryCMDBStoredDiscrepancyReportRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling getInventoryCMDBStoredDiscrepancyReport().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling getInventoryCMDBStoredDiscrepancyReport().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/discrepancy-report`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBDiscrepancyReportFromJSON(jsonValue));
        });
    }
    /**
     * Returns the latest persisted discrepancy report for the selected CMDB connection. If no report has been generated yet, the backend may derive one on demand and persist it.
     * Get the latest persisted CMDB discrepancy report for an inventory CMDB connection
     */
    getInventoryCMDBStoredDiscrepancyReport(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.getInventoryCMDBStoredDiscrepancyReportRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List temporal history for reconciled assertions
     */
    listCMDBAssertionHistoryRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listCMDBAssertionHistory().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listCMDBAssertionHistory().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/assertions/history`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CMDBEffectiveAssertionFromJSON));
        });
    }
    /**
     * List temporal history for reconciled assertions
     */
    listCMDBAssertionHistory(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listCMDBAssertionHistoryRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List versioned authority policies for the connection
     */
    listCMDBAuthorityPoliciesRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listCMDBAuthorityPolicies().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listCMDBAuthorityPolicies().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/authority-policies`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CMDBAuthorityPolicyFromJSON));
        });
    }
    /**
     * List versioned authority policies for the connection
     */
    listCMDBAuthorityPolicies(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listCMDBAuthorityPoliciesRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List explicit redundant dependency groups
     */
    listCMDBDependencyGroupsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listCMDBDependencyGroups().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listCMDBDependencyGroups().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/dependency-groups`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CMDBDependencyGroupFromJSON));
        });
    }
    /**
     * List explicit redundant dependency groups
     */
    listCMDBDependencyGroups(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listCMDBDependencyGroupsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List current effective reconciled assertions
     */
    listCMDBEffectiveAssertionsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listCMDBEffectiveAssertions().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listCMDBEffectiveAssertions().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/assertions`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CMDBEffectiveAssertionFromJSON));
        });
    }
    /**
     * List current effective reconciled assertions
     */
    listCMDBEffectiveAssertions(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listCMDBEffectiveAssertionsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List customer maturity declarations
     */
    listCMDBMaturityDeclarationsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listCMDBMaturityDeclarations().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listCMDBMaturityDeclarations().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/maturity-declarations`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CMDBMaturityDeclarationFromJSON));
        });
    }
    /**
     * List customer maturity declarations
     */
    listCMDBMaturityDeclarations(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listCMDBMaturityDeclarationsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List measured CMDB maturity history
     */
    listCMDBMaturityHistoryRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listCMDBMaturityHistory().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listCMDBMaturityHistory().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/maturity/history`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CMDBMeasuredMaturityFromJSON));
        });
    }
    /**
     * List measured CMDB maturity history
     */
    listCMDBMaturityHistory(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listCMDBMaturityHistoryRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List governed publication proposals for a CMDB connection
     */
    listCMDBPublicationProposalsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listCMDBPublicationProposals().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listCMDBPublicationProposals().');
            }
            const queryParameters = {};
            if (requestParameters['status'] != null) {
                queryParameters['status'] = requestParameters['status'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/publication/proposals`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CMDBPublicationProposalFromJSON));
        });
    }
    /**
     * List governed publication proposals for a CMDB connection
     */
    listCMDBPublicationProposals(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listCMDBPublicationProposalsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List the audit history for a reconciliation finding
     */
    listCMDBReconciliationFindingHistoryRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listCMDBReconciliationFindingHistory().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listCMDBReconciliationFindingHistory().');
            }
            if (requestParameters['findingId'] == null) {
                throw new runtime.RequiredError('findingId', 'Required parameter "findingId" was null or undefined when calling listCMDBReconciliationFindingHistory().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/findings/{findingId}/history`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"findingId"}}`, encodeURIComponent(String(requestParameters['findingId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CMDBFindingHistoryFromJSON));
        });
    }
    /**
     * List the audit history for a reconciliation finding
     */
    listCMDBReconciliationFindingHistory(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listCMDBReconciliationFindingHistoryRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List governed reconciliation findings
     */
    listCMDBReconciliationFindingsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listCMDBReconciliationFindings().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listCMDBReconciliationFindings().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/findings`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CMDBReconciliationFindingFromJSON));
        });
    }
    /**
     * List governed reconciliation findings
     */
    listCMDBReconciliationFindings(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listCMDBReconciliationFindingsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List source observations available to reconciliation
     */
    listCMDBReconciliationObservationsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listCMDBReconciliationObservations().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listCMDBReconciliationObservations().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/observations`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CMDBReconciliationObservationFromJSON));
        });
    }
    /**
     * List source observations available to reconciliation
     */
    listCMDBReconciliationObservations(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listCMDBReconciliationObservationsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * List reconciliation execution history
     */
    listCMDBReconciliationRunsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listCMDBReconciliationRuns().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listCMDBReconciliationRuns().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/runs`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CMDBReconciliationRunFromJSON));
        });
    }
    /**
     * List reconciliation execution history
     */
    listCMDBReconciliationRuns(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listCMDBReconciliationRunsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Returns discovered devices for the selected CMDB connection that currently need CMDB action, such as creating a missing CI or updating an existing matched CI.
     * List provider-neutral CMDB correction candidates
     */
    listInventoryCMDBActionItemsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling listInventoryCMDBActionItems().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling listInventoryCMDBActionItems().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/action-items`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => IntegrationConnectionCMDBUnboundDevicesResponseFromJSON(jsonValue));
        });
    }
    /**
     * Returns discovered devices for the selected CMDB connection that currently need CMDB action, such as creating a missing CI or updating an existing matched CI.
     * List provider-neutral CMDB correction candidates
     */
    listInventoryCMDBActionItems(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.listInventoryCMDBActionItemsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Preview reconciliation under a draft authority policy
     */
    previewCMDBAuthorityPolicyRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling previewCMDBAuthorityPolicy().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling previewCMDBAuthorityPolicy().');
            }
            if (requestParameters['policyId'] == null) {
                throw new runtime.RequiredError('policyId', 'Required parameter "policyId" was null or undefined when calling previewCMDBAuthorityPolicy().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/authority-policies/{policyId}/preview`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"policyId"}}`, encodeURIComponent(String(requestParameters['policyId']))),
                method: 'GET',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBAuthorityPolicyPreviewFromJSON(jsonValue));
        });
    }
    /**
     * Preview reconciliation under a draft authority policy
     */
    previewCMDBAuthorityPolicy(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.previewCMDBAuthorityPolicyRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Replace rules in a draft attribute mapping profile
     */
    replaceCMDBAttributeMappingRulesRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling replaceCMDBAttributeMappingRules().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling replaceCMDBAttributeMappingRules().');
            }
            if (requestParameters['profileId'] == null) {
                throw new runtime.RequiredError('profileId', 'Required parameter "profileId" was null or undefined when calling replaceCMDBAttributeMappingRules().');
            }
            if (requestParameters['cMDBAttributeMappingRulesRequest'] == null) {
                throw new runtime.RequiredError('cMDBAttributeMappingRulesRequest', 'Required parameter "cMDBAttributeMappingRulesRequest" was null or undefined when calling replaceCMDBAttributeMappingRules().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/attribute-mapping/{profileId}/rules`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"profileId"}}`, encodeURIComponent(String(requestParameters['profileId']))),
                method: 'PUT',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBAttributeMappingRulesRequestToJSON(requestParameters['cMDBAttributeMappingRulesRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBAttributeMappingProfileFromJSON(jsonValue));
        });
    }
    /**
     * Replace rules in a draft attribute mapping profile
     */
    replaceCMDBAttributeMappingRules(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.replaceCMDBAttributeMappingRulesRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Replace rules in a draft authority policy
     */
    replaceCMDBAuthorityPolicyRulesRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling replaceCMDBAuthorityPolicyRules().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling replaceCMDBAuthorityPolicyRules().');
            }
            if (requestParameters['policyId'] == null) {
                throw new runtime.RequiredError('policyId', 'Required parameter "policyId" was null or undefined when calling replaceCMDBAuthorityPolicyRules().');
            }
            if (requestParameters['cMDBAuthorityRulesRequest'] == null) {
                throw new runtime.RequiredError('cMDBAuthorityRulesRequest', 'Required parameter "cMDBAuthorityRulesRequest" was null or undefined when calling replaceCMDBAuthorityPolicyRules().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/authority-policies/{policyId}/rules`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"policyId"}}`, encodeURIComponent(String(requestParameters['policyId']))),
                method: 'PUT',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBAuthorityRulesRequestToJSON(requestParameters['cMDBAuthorityRulesRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBAuthorityPolicyFromJSON(jsonValue));
        });
    }
    /**
     * Replace rules in a draft authority policy
     */
    replaceCMDBAuthorityPolicyRules(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.replaceCMDBAuthorityPolicyRulesRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Replace explicit redundant dependency groups
     */
    replaceCMDBDependencyGroupsRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling replaceCMDBDependencyGroups().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling replaceCMDBDependencyGroups().');
            }
            if (requestParameters['cMDBDependencyGroupsRequest'] == null) {
                throw new runtime.RequiredError('cMDBDependencyGroupsRequest', 'Required parameter "cMDBDependencyGroupsRequest" was null or undefined when calling replaceCMDBDependencyGroups().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/dependency-groups`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'PUT',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBDependencyGroupsRequestToJSON(requestParameters['cMDBDependencyGroupsRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(CMDBDependencyGroupFromJSON));
        });
    }
    /**
     * Replace explicit redundant dependency groups
     */
    replaceCMDBDependencyGroups(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.replaceCMDBDependencyGroupsRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Queue provider-neutral reconciliation
     */
    runCMDBReconciliationRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling runCMDBReconciliation().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling runCMDBReconciliation().');
            }
            const queryParameters = {};
            if (requestParameters['mode'] != null) {
                queryParameters['mode'] = requestParameters['mode'];
            }
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBReconciliationRequestFromJSON(jsonValue));
        });
    }
    /**
     * Queue provider-neutral reconciliation
     */
    runCMDBReconciliation(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.runCMDBReconciliationRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Runs discrepancy analysis for the selected CMDB connection, persists the resulting report, and returns the latest snapshot.
     * Generate and persist a CMDB discrepancy report for an inventory CMDB connection
     */
    runInventoryCMDBDiscrepancyReportRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling runInventoryCMDBDiscrepancyReport().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling runInventoryCMDBDiscrepancyReport().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/discrepancy-report`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBDiscrepancyReportFromJSON(jsonValue));
        });
    }
    /**
     * Runs discrepancy analysis for the selected CMDB connection, persists the resulting report, and returns the latest snapshot.
     * Generate and persist a CMDB discrepancy report for an inventory CMDB connection
     */
    runInventoryCMDBDiscrepancyReport(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.runInventoryCMDBDiscrepancyReportRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Validate, activate, retire, or roll back an authority policy
     */
    transitionCMDBAuthorityPolicyRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling transitionCMDBAuthorityPolicy().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling transitionCMDBAuthorityPolicy().');
            }
            if (requestParameters['policyId'] == null) {
                throw new runtime.RequiredError('policyId', 'Required parameter "policyId" was null or undefined when calling transitionCMDBAuthorityPolicy().');
            }
            if (requestParameters['action'] == null) {
                throw new runtime.RequiredError('action', 'Required parameter "action" was null or undefined when calling transitionCMDBAuthorityPolicy().');
            }
            const queryParameters = {};
            const headerParameters = {};
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/authority-policies/{policyId}/{action}`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"policyId"}}`, encodeURIComponent(String(requestParameters['policyId']))).replace(`{${"action"}}`, encodeURIComponent(String(requestParameters['action']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBAuthorityPolicyFromJSON(jsonValue));
        });
    }
    /**
     * Validate, activate, retire, or roll back an authority policy
     */
    transitionCMDBAuthorityPolicy(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.transitionCMDBAuthorityPolicyRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Apply an authorized finding workflow transition
     */
    transitionCMDBReconciliationFindingRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling transitionCMDBReconciliationFinding().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling transitionCMDBReconciliationFinding().');
            }
            if (requestParameters['findingId'] == null) {
                throw new runtime.RequiredError('findingId', 'Required parameter "findingId" was null or undefined when calling transitionCMDBReconciliationFinding().');
            }
            if (requestParameters['cMDBFindingTransitionRequest'] == null) {
                throw new runtime.RequiredError('cMDBFindingTransitionRequest', 'Required parameter "cMDBFindingTransitionRequest" was null or undefined when calling transitionCMDBReconciliationFinding().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/reconciliation/findings/{findingId}/transition`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))).replace(`{${"findingId"}}`, encodeURIComponent(String(requestParameters['findingId']))),
                method: 'POST',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBFindingTransitionRequestToJSON(requestParameters['cMDBFindingTransitionRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBReconciliationFindingFromJSON(jsonValue));
        });
    }
    /**
     * Apply an authorized finding workflow transition
     */
    transitionCMDBReconciliationFinding(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.transitionCMDBReconciliationFindingRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Update governed publication policy for a CMDB connection
     */
    updateCMDBPublicationPolicyRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling updateCMDBPublicationPolicy().');
            }
            if (requestParameters['connectionId'] == null) {
                throw new runtime.RequiredError('connectionId', 'Required parameter "connectionId" was null or undefined when calling updateCMDBPublicationPolicy().');
            }
            if (requestParameters['cMDBPublicationPolicyUpdateRequest'] == null) {
                throw new runtime.RequiredError('cMDBPublicationPolicyUpdateRequest', 'Required parameter "cMDBPublicationPolicyUpdateRequest" was null or undefined when calling updateCMDBPublicationPolicy().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/devices/cmdb/connections/{connectionId}/publication/policy`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))).replace(`{${"connectionId"}}`, encodeURIComponent(String(requestParameters['connectionId']))),
                method: 'PUT',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBPublicationPolicyUpdateRequestToJSON(requestParameters['cMDBPublicationPolicyUpdateRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBPublicationPolicyFromJSON(jsonValue));
        });
    }
    /**
     * Update governed publication policy for a CMDB connection
     */
    updateCMDBPublicationPolicy(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.updateCMDBPublicationPolicyRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
    /**
     * Saves org-level matching and interpretation settings. This endpoint never grants CMDB publication authority; writeback is controlled only by the connection operating policy.
     * Update org CMDB reconciliation policy
     */
    updateCMDBReconciliationPolicyRaw(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParameters['orgId'] == null) {
                throw new runtime.RequiredError('orgId', 'Required parameter "orgId" was null or undefined when calling updateCMDBReconciliationPolicy().');
            }
            if (requestParameters['cMDBReconciliationPolicyUpdateRequest'] == null) {
                throw new runtime.RequiredError('cMDBReconciliationPolicyUpdateRequest', 'Required parameter "cMDBReconciliationPolicyUpdateRequest" was null or undefined when calling updateCMDBReconciliationPolicy().');
            }
            const queryParameters = {};
            const headerParameters = {};
            headerParameters['Content-Type'] = 'application/json';
            if (this.configuration && this.configuration.apiKey) {
                headerParameters["X-Api-Key"] = yield this.configuration.apiKey("X-Api-Key"); // ApiKeyAuth authentication
            }
            if (this.configuration && this.configuration.accessToken) {
                const token = this.configuration.accessToken;
                const tokenString = yield token("bearerAuth", []);
                if (tokenString) {
                    headerParameters["Authorization"] = `Bearer ${tokenString}`;
                }
            }
            const response = yield this.request({
                path: `/orgs/{orgId}/cmdb/reconciliation-policy`.replace(`{${"orgId"}}`, encodeURIComponent(String(requestParameters['orgId']))),
                method: 'PUT',
                headers: headerParameters,
                query: queryParameters,
                body: CMDBReconciliationPolicyUpdateRequestToJSON(requestParameters['cMDBReconciliationPolicyUpdateRequest']),
            }, initOverrides);
            return new runtime.JSONApiResponse(response, (jsonValue) => CMDBReconciliationPolicyFromJSON(jsonValue));
        });
    }
    /**
     * Saves org-level matching and interpretation settings. This endpoint never grants CMDB publication authority; writeback is controlled only by the connection operating policy.
     * Update org CMDB reconciliation policy
     */
    updateCMDBReconciliationPolicy(requestParameters, initOverrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.updateCMDBReconciliationPolicyRaw(requestParameters, initOverrides);
            return yield response.value();
        });
    }
}
/**
 * @export
 */
export const RunCMDBReconciliationModeEnum = {
    Incremental: 'incremental',
    Full: 'full'
};
/**
 * @export
 */
export const TransitionCMDBAuthorityPolicyActionEnum = {
    Validate: 'validate',
    Activate: 'activate',
    Retire: 'retire',
    Rollback: 'rollback'
};
